import { Button } from '@v2/ui/Button';
import { Calendar, Day } from '@v2/ui/Calendar';
import { ArrowLeft, ArrowRight } from '@v2/ui/Icon/Arrow';
import { CalendarIcon } from '@v2/ui/Icon/Time';
import { Popover, PopoverContent, PopoverTrigger } from '@v2/ui/Popover';
import { format, isSameDay, isSameMonth } from 'date-fns';
import { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';

interface DateRangePickerProps {
  selectedDateFilter?: DateRange;
  onDateChange: (dateFilter: DateRange) => void;
  defaultLabel?: string;
  compactMobile?: boolean;
  singleMonth?: boolean;
}

export function DateRangePicker({
  selectedDateFilter,
  onDateChange,
  defaultLabel = 'All dates',
  compactMobile = false,
  singleMonth = false,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [date, setDate] = useState<DateRange | undefined>(selectedDateFilter);

  // Another picker instance (e.g. the mobile Filters panel) can change the applied range.
  useEffect(() => {
    setDate(selectedDateFilter);
    if (selectedDateFilter?.from) setCurrentMonth(selectedDateFilter.from);
  }, [selectedDateFilter]);

  const handleDateChange = (newDate: DateRange | undefined) => {
    if (newDate?.from && newDate?.to && isSameDay(newDate.from, newDate.to))
      return;
    setDate(newDate);
  };

  const formatDateRange = () => {
    if (!date?.from) return defaultLabel;

    if (!date.to) {
      return format(date.from, 'MMM d, yyyy');
    }

    return `${format(date.from, 'MMM d')} - ${format(date.to, 'MMM d, yyyy')}`;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          aria-label={formatDateRange()}
          className={`flex items-center gap-x-2 font-normal ${
            compactMobile
              ? `h-11 w-full min-w-0 justify-start px-3 text-sm md:h-12 md:w-auto md:px-4 md:text-base ${date?.from ? 'col-span-2 md:col-auto' : ''}`
              : 'h-9 md:h-12'
          }`}
        >
          <CalendarIcon className="shrink-0 text-gray-800" />
          <span className={compactMobile ? 'min-w-0 truncate' : undefined}>
            {formatDateRange()}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={`w-auto p-0 ${compactMobile ? 'max-h-[var(--radix-popover-content-available-height)] max-w-[calc(100vw-2rem)] overflow-y-auto' : ''}`}
        align="start"
      >
        <Calendar
          month={currentMonth}
          onMonthChange={(month) => setCurrentMonth(month)}
          weekStartsOn={1}
          initialFocus
          mode="range"
          selected={date}
          onSelect={handleDateChange}
          disabled={{ before: new Date() }}
          components={{
            IconLeft: () => <ArrowLeft size="20" className="stroke-gray-800" />,
            IconRight: () => (
              <ArrowRight size="20" className="stroke-gray-800" />
            ),
            Day: ({ ...props }) => <Day {...props} />,
            Footer: ({ displayMonth }) => (
              <CalendarFooter
                onCancel={() => setIsOpen(false)}
                displayMonth={displayMonth}
                date={date}
                setDate={setDate}
                onConfirm={() => {
                  setIsOpen(false);
                  if (date) onDateChange(date);
                }}
                currentMonth={currentMonth}
                singleMonth={singleMonth}
              />
            ),
          }}
          numberOfMonths={singleMonth ? 1 : 2}
        />
      </PopoverContent>
    </Popover>
  );
}

function CalendarFooter({
  setDate,
  date,
  onConfirm,
  displayMonth,
  currentMonth,
  onCancel,
  singleMonth,
}: {
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  date: DateRange | undefined;
  onConfirm: () => void;
  displayMonth: Date | undefined;
  currentMonth: Date;
  onCancel: () => void;
  singleMonth: boolean;
}) {
  if (!displayMonth) return null;
  const isFirstMonth = isSameMonth(displayMonth, currentMonth);

  return (
    <tfoot>
      <tr>
        <td colSpan={7}>
          <div className="flex w-full items-center justify-between py-2">
            {isFirstMonth && (
              <Button
                variant="tertiary"
                className="text-gray-800"
                onClick={() => setDate({ from: undefined, to: undefined })}
              >
                Reset
              </Button>
            )}
            {(!isFirstMonth || singleMonth) && (
              <div className="ml-auto flex w-full justify-end space-x-3">
                <Button variant="secondary" onClick={onCancel}>
                  Cancel
                </Button>
                <Button disabled={!date?.from && !date?.to} onClick={onConfirm}>
                  Apply
                </Button>
              </div>
            )}
          </div>
        </td>
      </tr>
    </tfoot>
  );
}
